import type { UserRole } from '../../../utils/roleUtils';
import type { HubspotTicketDetail } from '../../../stores/hubspotManagementStore';
import { HubspotTicketDetailPanel } from '../../HubspotTicketDetailPanel';

interface HubspotTicketDetailSidebarProps {
  selectedTicketId: string | null;
  viewerRole: UserRole;
  viewerUserId: string;
  tickets: HubspotTicketDetail[];
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  onClose: () => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  onTicketUpdated?: () => Promise<void>;
}

/**
 * Below `xl` the inspector overlays the queue: full-screen on phones, a
 * right-hand sheet with a scrim on tablets. From `xl` up the queue collapses
 * into a navigator column and the inspector takes the remaining width.
 */
export function HubspotTicketDetailSidebar({
  selectedTicketId,
  viewerRole,
  viewerUserId,
  tickets,
  pagination,
  onClose,
  onNavigate,
  onTicketUpdated,
}: HubspotTicketDetailSidebarProps) {
  if (!selectedTicketId) {
    return null;
  }

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 z-10 animate-in bg-black/60 fade-in duration-200 xl:hidden"
      />
      <aside
        aria-label="Ticket details"
        className="absolute inset-y-0 right-0 z-20 flex min-h-0 w-full animate-in flex-col overflow-hidden bg-neutral-900 shadow-2xl slide-in-from-right duration-200 sm:w-[min(40rem,92vw)] sm:border-l sm:border-neutral-800 xl:static xl:w-auto xl:min-w-0 xl:flex-1 xl:animate-none xl:border-l-0 xl:shadow-none"
      >
        <HubspotTicketDetailPanel
          key={selectedTicketId}
          ticketId={selectedTicketId}
          viewerRole={viewerRole}
          viewerUserId={viewerUserId}
          onClose={onClose}
          currentIndex={tickets.findIndex(
            (ticket) => ticket.id === selectedTicketId,
          )}
          pageSize={tickets.length}
          total={pagination.total}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onNavigate={onNavigate}
          onTicketUpdated={onTicketUpdated}
        />
      </aside>
    </>
  );
}
